/**
 * RepConnect - Unified Provider Search Service
 *
 * Combines database search (registered providers) with NPI Registry search
 * Prioritizes registered providers and provides clear source indicators
 */

import { providerService, userService } from './supabaseData';
import { NPIAPI } from './npiAPI';
import { logger } from './logger';

/**
 * Unified Provider Search Service
 * Searches both database and NPI Registry, prioritizing registered providers
 */
export const UnifiedProviderSearch = {
  /**
   * Search for providers across database and NPI Registry
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.name - Provider name
   * @param {string} searchParams.location - Location (city, state, ZIP)
   * @param {string} searchParams.specialty - Medical specialty
   * @param {number} searchParams.limit - Max results per source
   * @param {number} searchParams.offset - Offset for pagination
   * @returns {Object} Combined search results with metadata
   */
  searchProviders: async (searchParams) => {
    const { name, location, specialty, limit = 20, offset = 0 } = searchParams;

    try {
      logger.debug('🔍 Starting unified provider search:', searchParams);

      // Phase 1: Search registered providers in database
      const databaseResults = await UnifiedProviderSearch.searchDatabaseProviders({
        name,
        location,
        specialty,
        limit: Math.floor(limit / 2) // Reserve half the results for database
      });

      // Phase 2: Search NPI Registry (only if we have search terms)
      let npiResults = [];
      if (name || location || specialty !== 'all') {
        npiResults = await UnifiedProviderSearch.searchNPIProviders({
          name,
          location,
          specialty,
          limit: limit - databaseResults.length, // Use remaining limit
          offset
        });
      }

      // Phase 3: Combine and deduplicate results
      const combinedResults = UnifiedProviderSearch.combineResults(
        databaseResults,
        npiResults
      );

      logger.debug(`📊 Search complete: ${databaseResults.length} registered, ${npiResults.length} NPI, ${combinedResults.length} total`);

      return {
        providers: combinedResults,
        registeredCount: databaseResults.length,
        npiCount: npiResults.length,
        total: combinedResults.length,
        hasMore: npiResults.length === (limit - databaseResults.length), // More NPI results available
        searchParams
      };

    } catch (error) {
      logger.error('Unified search error', { error: error.message });
      throw error;
    }
  },

  /**
   * Search registered providers in database
   */
  searchDatabaseProviders: async ({ name, location, specialty, limit }) => {
    try {
      logger.debug('🏥 Searching database providers...');

      let allProviders = [];

      // Search healthcare_providers table first
      const providers = await providerService.searchProviders(name || '');

      // Convert database providers to unified format
      const formattedProviders = providers.map(provider =>
        UnifiedProviderSearch.formatDatabaseProvider(provider)
      );

      allProviders = [...formattedProviders];

      // Also search user_profiles for customer type users (physicians without provider records)
      if (name) {
        try {
          // Import supabase directly from the module
          const { supabase } = await import('./supabase');
          logger.debug('🔍 Supabase client status:', !!supabase);

          if (supabase) {
            logger.debug('🔍 Searching Supabase user_profiles for customers...');

            // Try direct Supabase query first
            try {
              logger.debug('🔍 Searching for name:', name);

              // Split search terms and create flexible search query
              const searchTerms = name.trim().split(/\s+/);
              logger.debug('🔍 Split search terms:', searchTerms);

              let query = supabase
                .from('user_profiles')
                .select('*')
                .eq('user_type', 'customer');

              if (searchTerms.length === 1) {
                // Single term - search both first and last name
                const term = searchTerms[0];
                query = query.or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%`);
                logger.debug('🔍 Single term query: first_name ILIKE %' + term + '% OR last_name ILIKE %' + term + '%');
              } else {
                // Multiple terms - more flexible matching
                const searchConditions = [];

                // Try to match first term with first_name and second term with last_name
                if (searchTerms.length >= 2) {
                  searchConditions.push(`and(first_name.ilike.%${searchTerms[0]}%,last_name.ilike.%${searchTerms[1]}%)`);
                }

                // Also try each term individually against both names
                searchTerms.forEach(term => {
                  searchConditions.push(`first_name.ilike.%${term}%`);
                  searchConditions.push(`last_name.ilike.%${term}%`);
                });

                const orCondition = searchConditions.join(',');
                query = query.or(orCondition);
                logger.debug('🔍 Multi-term query:', orCondition);
              }

              const { data: userProfiles, error: userError } = await query.limit(10);

              logger.debug('🔍 Supabase user_profiles query result:', { data: userProfiles, error: userError });

              if (userError) {
                logger.error('Supabase query error details', {
                  code: userError.code,
                  message: userError.message,
                  details: userError.details,
                  hint: userError.hint
                });
              }

              if (!userProfiles || userProfiles.length === 0) {
                logger.warn('Query returned no results. This might be due to RLS policies or query syntax issues');

                // Try a simpler query to test RLS
                logger.debug('🔍 Testing simpler query without filters...');
                const { data: testQuery, error: testError } = await supabase
                  .from('user_profiles')
                  .select('id, first_name, last_name')
                  .limit(3);
                logger.debug('🔍 Simple query result:', { data: testQuery, error: testError });
              }

              if (userProfiles && userProfiles.length > 0) {
                logger.debug('🔍 Found matching customers:', userProfiles.map(p => ({ id: p.id, name: `${p.first_name} ${p.last_name}` })));
              }

              // Also log what customers exist in the table
              const { data: allCustomers } = await supabase
                .from('user_profiles')
                .select('id, first_name, last_name, user_type')
                .eq('user_type', 'customer')
                .limit(10);
              logger.debug('🔍 All customers in user_profiles:', allCustomers);
              logger.debug('🔍 Expected to find customers with "test" in name:');
              allCustomers?.forEach(c => {
                const fullName = `${c.first_name} ${c.last_name}`;
                const shouldMatch = fullName.toLowerCase().includes(name.toLowerCase());
                logger.debug(`  - ${fullName} → Should match: ${shouldMatch}`);
              });

              if (!userError && userProfiles && userProfiles.length > 0) {
                logger.debug(`✅ Found ${userProfiles.length} customer profiles in Supabase matching "${name}"`);

                // Convert user profiles to provider format
                const userProviders = userProfiles.map(profile =>
                  UnifiedProviderSearch.formatUserProfileAsProvider(profile)
                );

                // Merge with existing providers, avoiding duplicates
                userProviders.forEach(userProvider => {
                  const exists = allProviders.some(existing =>
                    existing.userId === userProvider.userId
                  );
                  if (!exists) {
                    allProviders.push(userProvider);
                  }
                });
              } else {
                logger.debug('📭 No customer profiles found via direct Supabase query (likely RLS restrictions)');
                logger.debug('🔄 Will try userService fallback method below...');
              }
            } catch (directQueryError) {
              logger.error('Direct Supabase query failed', { error: directQueryError.message });
            }

            // Primary method: use userService since RLS blocks direct queries
            try {
              logger.debug('🔍 Using userService.getAllUsers() as primary method (RLS workaround)...');

              // Check current auth context
              const { data: { user } } = await supabase.auth.getUser();
              logger.debug('🔍 Current auth user:', user);
              logger.debug('🔍 User metadata:', user?.user_metadata);
              logger.debug('🔍 App metadata:', user?.app_metadata);

              const customerUsers = await userService.getCustomersOnly();
              logger.debug(`🔍 userService.getCustomersOnly() returned ${customerUsers.length} customer users`);
              logger.debug('🔍 Customer users from userService:', customerUsers.map(u => ({
                id: u.id,
                name: `${u.first_name} ${u.last_name}`,
                type: u.user_type
              })));

              // Apply name filtering to customers
              const matchingCustomers = customerUsers.filter(user => {
                logger.debug(`  - Customer: ${user.first_name} ${user.last_name}, Type: ${user.user_type}`);
                if (user.user_type !== 'customer') return false;

                const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
                const searchLower = name.toLowerCase();

                return fullName.includes(searchLower) ||
                       (user.first_name && user.first_name.toLowerCase().includes(searchLower)) ||
                       (user.last_name && user.last_name.toLowerCase().includes(searchLower));
              });

              logger.debug(`🔍 Found ${matchingCustomers.length} matching customer users via userService`);

              // If no authenticated Supabase user, fall back to demo customers for development
              if (!user && matchingCustomers.length === 0 && name.toLowerCase().includes('test')) {
                logger.debug('🔧 No Supabase authentication - using demo customers for development');
                const demoCustomers = [
                  { id: 'demo-test-user', first_name: 'Test', last_name: 'User', user_type: 'customer', email: 'test.user@example.com', company: 'Demo Healthcare' },
                  { id: 'demo-test-doc', first_name: 'Test', last_name: 'Doc', user_type: 'customer', email: 'test.doc@example.com', company: 'Clovis Community Hospital' },
                  { id: 'demo-test-doc2', first_name: 'Test', last_name: 'Doc2', user_type: 'customer', email: 'test.doc2@example.com', company: 'Saint Agnes' }
                ];

                demoCustomers.forEach(demoUser => {
                  const fullName = `${demoUser.first_name} ${demoUser.last_name}`;
                  const searchLower = name.toLowerCase();

                  if (fullName.toLowerCase().includes(searchLower) ||
                      demoUser.first_name.toLowerCase().includes(searchLower) ||
                      demoUser.last_name.toLowerCase().includes(searchLower)) {

                    const userProvider = UnifiedProviderSearch.formatUserProfileAsProvider(demoUser);
                    const exists = allProviders.some(existing => existing.userId === userProvider.userId);
                    if (!exists) {
                      allProviders.push(userProvider);
                      logger.debug(`✅ Added demo customer: ${fullName} (no Supabase auth)`);
                    }
                  }
                });
              }

              matchingCustomers.forEach(user => {
                const userProvider = UnifiedProviderSearch.formatUserProfileAsProvider(user);
                const exists = allProviders.some(existing => existing.userId === userProvider.userId);
                if (!exists) {
                  allProviders.push(userProvider);
                }
              });

            } catch (userServiceError) {
              logger.error('userService.getCustomersOnly() failed', { error: userServiceError.message });
            }

          } else {
            logger.debug('⚠️ Supabase client not available, trying localStorage fallback...');
          }

          // Always try localStorage as fallback (for development/offline mode)
          logger.debug('🔄 Also searching localStorage for customer users...');
          const localUserProfiles = UnifiedProviderSearch.searchLocalStorageUsers(name);

          if (localUserProfiles.length > 0) {
            logger.debug(`✅ Found ${localUserProfiles.length} customer profiles in localStorage`);

            // Convert local user profiles to provider format
            const localUserProviders = localUserProfiles.map(profile =>
              UnifiedProviderSearch.formatUserProfileAsProvider(profile)
            );

            // Merge with existing providers
            localUserProviders.forEach(userProvider => {
              const exists = allProviders.some(existing =>
                existing.userId === userProvider.userId
              );
              if (!exists) {
                allProviders.push(userProvider);
              }
            });
          }

        } catch (userSearchError) {
          logger.error('Error searching user profiles, falling back to localStorage only', { error: userSearchError.message });

          // Final fallback: localStorage only
          const localUserProfiles = UnifiedProviderSearch.searchLocalStorageUsers(name);
          const localUserProviders = localUserProfiles.map(profile =>
            UnifiedProviderSearch.formatUserProfileAsProvider(profile)
          );

          localUserProviders.forEach(userProvider => {
            const exists = allProviders.some(existing =>
              existing.userId === userProvider.userId
            );
            if (!exists) {
              allProviders.push(userProvider);
            }
          });
        }
      }

      // Apply filters to all providers
      const filteredProviders = allProviders.filter(provider => {
        // Apply additional filters
        const matchesLocation = !location ||
          provider.address?.toLowerCase().includes(location.toLowerCase()) ||
          provider.city?.toLowerCase().includes(location.toLowerCase()) ||
          provider.state?.toLowerCase().includes(location.toLowerCase());

        const matchesSpecialty = !specialty || specialty === 'all' ||
          provider.specialty?.toLowerCase().includes(specialty.toLowerCase());

        return matchesLocation && matchesSpecialty;
      });

      logger.debug(`✅ Found ${filteredProviders.length} total registered providers`);
      return filteredProviders.slice(0, limit);

    } catch (error) {
      logger.error('Database search error', { error: error.message });
      return [];
    }
  },

  /**
   * Search NPI Registry for external providers
   */
  searchNPIProviders: async ({ name, location, specialty, limit, offset }) => {
    try {
      logger.debug('🌐 Searching NPI Registry...');

      const npiResults = await NPIAPI.searchProviders({
        name,
        location,
        specialty,
        limit,
        offset
      });

      // Convert NPI providers to unified format
      const formattedProviders = npiResults.providers.map(provider =>
        UnifiedProviderSearch.formatNPIProvider(provider)
      );

      logger.debug(`✅ Found ${formattedProviders.length} NPI providers`);
      return formattedProviders;

    } catch (error) {
      logger.error('NPI search error', { error: error.message });
      return [];
    }
  },

  /**
   * Format user profile (customer) to unified provider format
   */
  formatUserProfileAsProvider: (userProfile) => {
    return {
      id: userProfile.id, // Use original UUID for Supabase compatibility
      originalId: userProfile.id,
      source: 'registered',
      isRegistered: true,

      // Basic info from user profile
      name: `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || 'Unknown Provider',
      specialty: userProfile.specialty || userProfile.title || 'General Practice',
      hospital: userProfile.company || 'Private Practice',

      // Contact info
      phone: userProfile.phone || 'Contact for phone',
      email: userProfile.email || 'Available after connection',
      address: userProfile.location || 'Address not specified',
      location: userProfile.location || 'Not specified',
      city: userProfile.city || 'Not specified',
      state: userProfile.state || 'Not specified',

      // NPI number if available
      npi_number: userProfile.npi_number || null,

      // RepConnect specific
      userId: userProfile.id,
      preferredTime: 'lunch', // Default
      rating: 5.0, // Default high rating for registered users

      // Availability
      isAvailable: true,
      lastLoginDate: userProfile.updated_at,

      // Visual indicators
      registrationStatus: 'verified',
      connectionStatus: 'ready',

      // Original data for reference
      _originalData: userProfile,
      _isUserProfile: true // Flag to distinguish from healthcare_providers
    };
  },

  /**
   * Format database provider to unified format
   */
  formatDatabaseProvider: (dbProvider) => {
    const userProfile = dbProvider.user_profiles;

    return {
      id: userProfile?.id || `db_${dbProvider.id}`, // Use user UUID if available, fallback to prefixed
      originalId: dbProvider.id,
      source: 'RepConnect Database',
      isRegistered: true,

      // Basic info
      name: dbProvider.practice_name ||
            (userProfile ? `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() : 'Unknown Provider'),
      specialty: dbProvider.specialty || 'Not specified',
      hospital: dbProvider.practice_name || 'Private Practice',

      // Contact info
      phone: dbProvider.phone || userProfile?.phone || 'Contact for phone',
      email: userProfile?.email || 'Available after connection',
      address: UnifiedProviderSearch.formatAddress(dbProvider),
      city: dbProvider.city,
      state: dbProvider.state,

      // RepConnect specific
      userId: dbProvider.user_id,
      preferredTime: UnifiedProviderSearch.getPreferredTime(dbProvider),
      rating: 5.0, // Default high rating for registered providers

      // Availability
      isAvailable: true,
      lastLoginDate: userProfile?.updated_at,

      // Visual indicators
      registrationStatus: 'verified',
      connectionStatus: 'ready',

      // Original data for reference
      _originalData: dbProvider
    };
  },

  /**
   * Format NPI provider to unified format
   */
  formatNPIProvider: (npiProvider) => {
    // Format location from city and state
    const location = [npiProvider.city, npiProvider.state].filter(Boolean).join(', ') || 'Not specified';

    return {
      id: `npi_${npiProvider.npi}`,
      originalId: npiProvider.npi,
      source: 'npi',
      isRegistered: false,

      // Basic info from NPI
      name: npiProvider.name,
      specialty: npiProvider.specialty,
      hospital: npiProvider.hospital || npiProvider.organization || 'Private Practice',

      // Contact info
      phone: npiProvider.phone || 'Contact for phone',
      email: 'Not available (invite to RepConnect)',
      address: npiProvider.address,
      location: location,
      city: npiProvider.city,
      state: npiProvider.state,

      // NPI specific
      npi_number: npiProvider.npi,
      npi: npiProvider.npi,
      credential: npiProvider.credential,
      preferredTime: 'lunch', // Default assumption
      rating: 4.0, // Default rating for NPI providers

      // Availability (unknown for external providers)
      isAvailable: null,

      // Visual indicators
      registrationStatus: 'unregistered',
      connectionStatus: 'invite_required',

      // Actions
      canInvite: true,
      inviteMessage: 'Invite to RepConnect',

      // Original data for reference
      _originalData: npiProvider
    };
  },

  /**
   * Combine and deduplicate results from both sources
   */
  combineResults: (databaseResults, npiResults) => {
    const combined = [...databaseResults];

    // Add NPI results that don't duplicate database results
    npiResults.forEach(npiProvider => {
      const isDuplicate = databaseResults.some(dbProvider =>
        UnifiedProviderSearch.isDuplicateProvider(dbProvider, npiProvider)
      );

      if (!isDuplicate) {
        combined.push(npiProvider);
      } else {
        logger.debug(`🔄 Filtered duplicate: ${npiProvider.name} (already registered)`);
      }
    });

    // Sort: registered providers first, then by rating, then by name
    return combined.sort((a, b) => {
      // Registered providers always come first
      if (a.isRegistered && !b.isRegistered) return -1;
      if (!a.isRegistered && b.isRegistered) return 1;

      // Within same registration status, sort by rating then name
      if (b.rating !== a.rating) return b.rating - a.rating;
      return a.name.localeCompare(b.name);
    });
  },

  /**
   * Check if two providers are likely the same person
   * Priority: NPI number match (most reliable) > Name matching (fallback)
   */
  isDuplicateProvider: (dbProvider, npiProvider) => {
    // PRIMARY CHECK: NPI number matching (most reliable - unique identifier)
    // If the database provider has claimed an NPI number, check if it matches
    if (dbProvider.npi_number && npiProvider.npi) {
      if (dbProvider.npi_number === npiProvider.npi) {
        logger.debug('NPI match found - provider already registered', {
          npi: npiProvider.npi,
          name: dbProvider.name
        });
        return true; // This NPI is claimed - don't show from API
      }
    }

    // FALLBACK: Name-based matching (for providers who haven't entered NPI)
    const dbName = dbProvider.name.toLowerCase().trim();
    const npiName = npiProvider.name.toLowerCase().trim();

    // Check for exact name match
    if (dbName === npiName) return true;

    // Check for name similarity (last name + first initial)
    const dbParts = dbName.split(' ');
    const npiParts = npiName.split(' ');

    if (dbParts.length >= 2 && npiParts.length >= 2) {
      const dbLastName = dbParts[dbParts.length - 1];
      const npiLastName = npiParts[npiParts.length - 1];
      const dbFirstInitial = dbParts[0][0];
      const npiFirstInitial = npiParts[0][0];

      if (dbLastName === npiLastName && dbFirstInitial === npiFirstInitial) {
        return true;
      }
    }

    return false;
  },

  /**
   * Helper: Format address from database provider
   */
  formatAddress: (dbProvider) => {
    // Check if we have full_address first, then build from parts
    if (dbProvider.full_address) {
      return dbProvider.full_address;
    }

    const parts = [
      dbProvider.street_address || dbProvider.address, // Try both field names
      dbProvider.city,
      dbProvider.state,
      dbProvider.zip_code
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'Address not available';
  },

  /**
   * Helper: Get preferred meeting time from database provider
   */
  getPreferredTime: (dbProvider) => {
    if (dbProvider.preferred_meeting_times && dbProvider.preferred_meeting_times.length > 0) {
      return dbProvider.preferred_meeting_times[0];
    }
    return 'lunch'; // Default
  },

  /**
   * Search localStorage for customer users (fallback when no Supabase)
   */
  searchLocalStorageUsers: (searchName) => {
    try {
      logger.debug(`🔍 Searching localStorage for users matching "${searchName}"`);

      // Get current user first
      const currentUserData = localStorage.getItem('repconnect_current_user');
      const foundUsers = [];

      if (currentUserData) {
        const { user, profile } = JSON.parse(currentUserData);
        logger.debug('🔍 Current user data:', { user, profile });

        // Check if current user matches search and is a customer
        if (profile) {
          logger.debug(`🔍 Profile user_type: "${profile.user_type}"`);

          if (profile.user_type === 'customer') {
            const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
            const searchLower = searchName.toLowerCase();

            logger.debug(`🔍 Checking name "${fullName}" against search "${searchName}"`);
            logger.debug(`🔍 Profile fields:`, {
              first_name: profile.first_name,
              last_name: profile.last_name,
              email: profile.email
            });

            if (
              fullName.toLowerCase().includes(searchLower) ||
              (profile.first_name && profile.first_name.toLowerCase().includes(searchLower)) ||
              (profile.last_name && profile.last_name.toLowerCase().includes(searchLower)) ||
              (profile.email && profile.email.toLowerCase().includes(searchLower))
            ) {
              logger.debug(`✅ Found current user "${fullName}" matching search`);
              foundUsers.push(profile);
            } else {
              logger.debug(`❌ Current user "${fullName}" does not match search "${searchName}"`);
            }
          } else {
            logger.debug(`❌ Current user is not a customer (type: ${profile.user_type})`);
          }
        } else {
          logger.debug('❌ No profile found in current user data');
        }
      } else {
        logger.debug('❌ No current user data found in localStorage');
      }

      // Also search for other registered users in localStorage
      // Check for users stored with auth_user_ prefix
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('auth_user_')) {
          try {
            const userData = localStorage.getItem(key);
            if (userData) {
              const { user: storedUser, profile: storedProfile } = JSON.parse(userData);

              if (storedProfile && storedProfile.user_type === 'customer') {
                const fullName = `${storedProfile.first_name || ''} ${storedProfile.last_name || ''}`.trim();
                const searchLower = searchName.toLowerCase();

                logger.debug(`🔍 Checking stored user "${fullName}" (${storedProfile.email})`);

                if (
                  fullName.toLowerCase().includes(searchLower) ||
                  (storedProfile.first_name && storedProfile.first_name.toLowerCase().includes(searchLower)) ||
                  (storedProfile.last_name && storedProfile.last_name.toLowerCase().includes(searchLower)) ||
                  (storedProfile.email && storedProfile.email.toLowerCase().includes(searchLower))
                ) {
                  logger.debug(`✅ Found stored user "${fullName}" matching search`);
                  const isDuplicate = foundUsers.some(existing => existing.id === storedProfile.id);
                  if (!isDuplicate) {
                    foundUsers.push(storedProfile);
                  }
                }
              }
            }
          } catch (error) {
            logger.warn('Error parsing stored user data', { key, error: error.message });
          }
        }
      }

      logger.debug(`📊 Found ${foundUsers.length} local users matching "${searchName}"`);
      return foundUsers;

    } catch (error) {
      logger.error('Error searching localStorage users', { error: error.message });
      return [];
    }
  },

  /**
   * Search for specific provider by ID across both sources
   */
  getProviderById: async (providerId) => {
    try {
      if (providerId.startsWith('db_')) {
        // Database provider
        const dbId = providerId.replace('db_', '');
        const provider = await providerService.getProviderById(dbId);
        return provider ? UnifiedProviderSearch.formatDatabaseProvider(provider) : null;
      } else if (providerId.startsWith('npi_')) {
        // NPI provider - would need to implement NPI lookup by ID
        logger.debug('🔍 NPI provider lookup not yet implemented');
        return null;
      }

      return null;
    } catch (error) {
      logger.error('Error getting provider by ID', { error: error.message });
      return null;
    }
  }
};

export default UnifiedProviderSearch;